/** "Change signature" panel.
 *
 *  Drop in a PNG of a real ink-on-paper signature. The upload mutation
 *  replaces whatever was previously stored; on success the React Query
 *  cache for ``signatureKeys`` is invalidated and any open
 *  ``SignaturePreviewImage`` consumers refresh automatically.
 */

'use client';

import { useRef, useState, type ChangeEvent } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';

import { useSignatureUploadMutation } from '../model/useSignatureMutations';
import { useSignatureQuery } from '../model/useSignatureQuery';

import { SignaturePreviewImage } from './SignaturePreviewImage';

interface SignaturePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Strip the ``data:<mime>;base64,`` prefix that ``FileReader.readAsDataURL``
 *  emits — the upload endpoint expects raw base64. Same shape as the
 *  PA-attachment upload helper in ``PriorAuthActionWizard``. */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIdx = dataUrl.indexOf(',');
  return commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read signature file'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file reader result'));
        return;
      }
      resolve(stripDataUrlPrefix(result));
    };
    reader.readAsDataURL(file);
  });
}

const ACCEPTED_MIME = 'image/png';
/** Mirrors the server-side ``MAX_SIGNATURE_UPLOAD_BYTES`` -- duplicated
 *  here so we can short-circuit obviously-too-big files before paying
 *  the FileReader + base64 round trip. */
const CLIENT_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

export function SignaturePanel({ open, onOpenChange }: SignaturePanelProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {open && <SignaturePanelBody onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

interface SignaturePanelBodyProps {
  onClose: () => void;
}

function SignaturePanelBody({ onClose }: SignaturePanelBodyProps) {
  const sigQuery = useSignatureQuery();
  const uploadMutation = useSignatureUploadMutation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const signature = sigQuery.data ?? null;

  const handleFilePick = async (event: ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = event.target.files?.[0];
    // Always clear the input value so picking the same file twice
    // re-fires the change event (browsers de-dupe by default).
    event.target.value = '';
    if (!file) return;

    if (file.size > CLIENT_MAX_UPLOAD_BYTES) {
      setUploadError('File is too large; please pick an image under 2 MB.');
      return;
    }
    if (file.type && file.type !== ACCEPTED_MIME) {
      setUploadError('Only PNG signatures are supported.');
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      uploadMutation.mutate(
        { content_base64: base64 },
        {
          onSuccess: () => onClose(),
          onError: (err) => {
            setUploadError(
              err instanceof Error ? err.message : 'Upload failed; please try a different image.'
            );
          },
        }
      );
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Could not read the file.');
    }
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Change signature</DialogTitle>
        <DialogDescription>
          Upload a PNG of your handwritten signature. The image is embedded into every Letter of
          Medical Necessity you sign.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4 py-2">
        {signature ? (
          <div className="rounded-md border border-border p-3 flex flex-col gap-2">
            <div className="text-xs text-muted-foreground">Currently saved</div>
            <SignaturePreviewImage signature={signature} />
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
            No signature uploaded yet.
          </div>
        )}

        {uploadError && (
          <p className="text-xs text-destructive" role="alert">
            {uploadError}
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIME}
          className="hidden"
          onChange={handleFilePick}
        />
      </div>

      <DialogFooter className="mt-2">
        <Button variant="ghost" onClick={onClose} disabled={uploadMutation.isPending}>
          Cancel
        </Button>
        <Button onClick={triggerFilePicker} disabled={uploadMutation.isPending}>
          {uploadMutation.isPending
            ? 'Uploading…'
            : signature
              ? 'Replace signature'
              : 'Upload signature'}
        </Button>
      </DialogFooter>
    </>
  );
}
